import { useEffect, useState } from 'react';

/**
 * Whether the server is currently reachable.
 *
 * `navigator.onLine` only knows whether there is a network interface, not
 * whether our server answers — a captive portal at a venue looks online and is
 * not. So the events are used as a hint to re-probe, and the probe is what
 * decides.
 */
export function useOnline(probePath = '/up', intervalMs = 60_000): boolean {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);

  useEffect(() => {
    let cancelled = false;

    async function probe(): Promise<void> {
      if (!navigator.onLine) {
        if (!cancelled) setOnline(false);
        return;
      }
      try {
        await fetch(probePath, { method: 'GET', cache: 'no-store' });
        if (!cancelled) setOnline(true);
      } catch {
        if (!cancelled) setOnline(false);
      }
    }

    void probe();
    const timer = setInterval(() => void probe(), intervalMs);
    window.addEventListener('online', () => void probe());
    window.addEventListener('offline', () => setOnline(false));

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [probePath, intervalMs]);

  return online;
}
